import type { MaybeValidProps } from "@core/domain/models/maybe-valid.ts";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const DEFAULT_PER_PAGE = 10;

export const PaginationSchema = z.object({
    count: z.number().nonnegative(),
    current: z.number().nonnegative(),
    pages: z.number().nonnegative(),
    perPage: z.number().nonnegative().default(DEFAULT_PER_PAGE),
});

export type PaginationInputProps = z.input<typeof PaginationSchema>;
export type PaginationProps = z.output<typeof PaginationSchema>;

export class Pagination {
    private static readonly NO_PAGINATION_PAGE = 1;
    private static readonly NO_PAGINATION_PAGE_SIZE = 1000;

    static NoPagination(): Pagination {
        const result = Pagination.create({
            count: 0,
            current: Pagination.NO_PAGINATION_PAGE,
            pages: 0,
            perPage: Pagination.NO_PAGINATION_PAGE_SIZE,
        });

        if (result.isOk()) {
            return result.value;
        }

        throw new Error("Unexpected error creating Pagination");
    }

    static Default(): Pagination {
        const result = Pagination.create({
            count: 0,
            current: Pagination.NO_PAGINATION_PAGE,
            pages: 1,
            perPage: Pagination.NO_PAGINATION_PAGE_SIZE,
        });

        if (result.isOk()) {
            return result.value;
        }

        throw new Error("Unexpected error creating Pagination");
    }

    private constructor(readonly props: Readonly<PaginationProps>) {}

    get count(): number {
        return this.props.count;
    }

    get current(): number {
        return this.props.current;
    }

    get pages(): number {
        return this.props.pages;
    }

    get perPage(): number {
        return this.props.perPage;
    }

    static validate(
        props: MaybeValidProps<PaginationInputProps>,
    ): Result<PaginationProps, z.ZodError<PaginationProps>> {
        const parsed = PaginationSchema.safeParse(props);

        if (!parsed.success) {
            console.error(parsed.error.issues);
            return err(parsed.error);
        }

        return ok(parsed.data);
    }

    static create(
        props: PaginationInputProps,
    ): Result<Pagination, z.ZodError<PaginationProps>> {
        return Pagination.validate(props).map(
            (paginationProps) => new Pagination(paginationProps),
        );
    }

    update(pagination: PaginationInputProps): Pagination {
        const updatedPagination = Pagination.create({
            count: pagination.count,
            current: pagination.current,
            pages: pagination.pages,
            perPage: pagination.perPage,
        });

        // If the new data is invalid, return the current pagination (prevent change)
        return updatedPagination.match(
            (paginationToUpdate) => paginationToUpdate,
            (error) => {
                console.error(error.issues);
                return this;
            },
        );
    }

    reset(): Pagination {
        return Pagination.Default();
    }

    toCreateProps(): PaginationInputProps {
        return {
            count: this.props.count,
            current: this.props.current,
            pages: this.props.pages,
            perPage: this.props.perPage,
        };
    }
}
